import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useCreateCollector } from "@/api/collector/collector";
import { collectorSchema } from "@/api/collector/collector-validation";

const schema = collectorSchema.pick({
  collectionneursDescriptionPrenom: true,
  collectionneursDescriptionNom: true,
});
type FormType = z.infer<typeof schema>;

type NewCollectorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewCollectorDialog = ({
  onOpenChange,
  open,
}: NewCollectorDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: {
      collectionneursDescriptionPrenom: "",
      collectionneursDescriptionNom: "",
    },
  });
  const { mutateAsync } = useCreateCollector();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType) => {
    try {
      const result = await mutateAsync(data);
      navigate(`/edit/collector/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("collectionneursDescriptionNom", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createCcollector")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="collectionneursDescriptionPrenom"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("collectionneursDescriptionPrenom")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="collectionneursDescriptionNom"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{t("collectionneursDescriptionNom")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewCollectorDialog;
